import { useClipboard } from '@mantine/hooks'
import { cva } from 'class-variance-authority'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from 'lib/utils'
import { CheckIcon, CopyIcon } from 'lucide-react'
import type React from 'react'
import { Button, type ButtonProps } from '../primitives/button'

interface ClipboardCopyButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size' | 'onClick'> {
  textToCopy: string
  tooltipText?: string // Optional label for screen readers
  label?: string // Optional label for screen readers
  onCopy?: () => void // Optional callback after copy
  className?: string
  variant?: ButtonProps['variant']
  position?: 'topRight' | 'bottomRight' | 'default'
  size?: ButtonProps['size']
  iconSize?: number
}

export const ClipboardCopyButton: React.FC<ClipboardCopyButtonProps> = ({
  textToCopy,
  tooltipText,
  label,
  onCopy,
  className,
  size = 'icon',
  variant = 'ghost',
  position = 'default',
  iconSize = 16,
  ...props
}) => {
  const clipboard = useClipboard({ timeout: 3000 })

  const buttonVariants = cva('relative', {
    variants: {
      position: {
        default: 'size-4',
        topRight:
          'absolute top-3 right-3 h-5 w-5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 transition-opacity',
        bottomRight:
          'absolute bottom-3 right-3 h-5 w-5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 transition-opacity',
      },
    },
    defaultVariants: {
      position: 'default',
    },
  })

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    clipboard.copy(textToCopy)
    onCopy?.()
  }

  return (
    <Button
      variant={variant}
      size={size}
      className={cn(buttonVariants({ position }), className)}
      type="button"
      tooltipContent={clipboard.copied ? 'Copied' : tooltipText}
      onClick={handleCopy}
      {...props}
    >
      <div className="overflow-hidden" style={{ width: iconSize, height: iconSize }}>
        <CopyIcon size={iconSize} className={clipboard.copied ? 'hidden' : ''} />
        <AnimatePresence>
          {clipboard.copied && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <CheckIcon size={iconSize} className="text-status-success" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* Optional label for screen readers */}
      {label && <span className="ml-2">{label}</span>}
    </Button>
  )
}
